import { useState } from 'react';
import { toast } from 'sonner';
import { Pencil, Trash2, Download, MessageCircle, Send, Facebook } from 'lucide-react';
import { downloadResultImage, shareResultImage } from './resultImage';

const LETTERS = 'ABCDEFGHJKL'.split('');

export function ShowSlotCard({ show, label, theme, existing, onPublish, onEdit, onDelete, onTouch }) {
  const [letter, setLetter] = useState(existing?.letter || null);
  const [digits, setDigits] = useState(existing ? existing.digits.split('') : ['', '', '', '', '']);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const setDigit = (i, v) => {
    if (v !== '' && !/^\d$/.test(v)) return;
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v !== '' && i < 4) document.getElementById(`digit-${show}-${i + 1}`)?.focus();
  };

  const submit = async () => {
    if (!letter) return toast.error('Letter select பண்ணுங்க');
    if (digits.some((d) => d === '')) return toast.error('5 numbers-um fill பண்ணுங்க');
    setBusy(true);
    try {
      if (existing && editing) {
        await onEdit(existing.id, { show, letter, digits: digits.join('') });
        setEditing(false);
      } else {
        await onPublish({ show, letter, digits: digits.join('') });
      }
    } finally {
      setBusy(false);
    }
  };

  const startEdit = () => {
    setLetter(existing.letter);
    setDigits(existing.digits.split(''));
    setEditing(true);
  };

  const locked = existing && !editing;

  return (
    <div
      className="border border-white/10 bg-black/30 p-5"
      data-testid={`slot-card-${show}`}
      onClick={() => onTouch?.(show)}
    >
      <div className="mb-4 flex items-center justify-between">
        <span className="font-display text-lg uppercase tracking-[0.15em]" style={{ color: theme }}>
          {label} Show
        </span>
        {existing && (
          <span className="rounded-full border border-green-500/40 px-3 py-0.5 text-[10px] uppercase tracking-[0.2em] text-green-400" data-testid={`slot-status-${show}`}>
            Published: {existing.letter} {existing.digits}
          </span>
        )}
      </div>

      {locked ? (
        <>
          <div className="flex items-center gap-3">
            <p className="font-display flex-1 text-3xl tracking-[0.2em] text-[#F5C542]" data-testid={`slot-result-${show}`}>
              {existing.letter} — {existing.digits}
            </p>
            <button type="button" onClick={startEdit} data-testid={`slot-edit-${show}`} className="flex items-center gap-1.5 border border-white/20 px-3 py-2 text-xs uppercase tracking-[0.15em] text-white hover:border-[#F5C542] hover:text-[#F5C542]">
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
            <button type="button" onClick={() => onDelete(existing.id, show)} data-testid={`slot-delete-${show}`} className="flex items-center gap-1.5 border border-red-500/40 px-3 py-2 text-xs uppercase tracking-[0.15em] text-red-400 hover:bg-red-500/10">
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={async () => {
                try {
                  await downloadResultImage(show, existing.letter, existing.digits, existing.date);
                  toast.success('Result image downloaded');
                } catch {
                  toast.error('Download failed');
                }
              }}
              data-testid={`slot-download-${show}`}
              className="flex items-center gap-1.5 bg-[#F5C542] px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-black hover:bg-[#FFDD75]"
            >
              <Download className="h-3.5 w-3.5" /> Download
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  const r = await shareResultImage(show, existing.letter, existing.digits, existing.date, 'whatsapp');
                  if (r === 'fallback') toast.info('Image downloaded — WhatsApp-la attach பண்ணுங்க');
                } catch {
                  toast.error('Share failed');
                }
              }}
              data-testid={`slot-whatsapp-${show}`}
              className="flex items-center gap-1.5 border border-green-500/50 px-4 py-2 text-xs uppercase tracking-[0.15em] text-green-400 hover:bg-green-500/10"
            >
              <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  const r = await shareResultImage(show, existing.letter, existing.digits, existing.date, 'telegram');
                  if (r === 'fallback') toast.info('Image downloaded — Telegram-la attach பண்ணுங்க');
                } catch {
                  toast.error('Share failed');
                }
              }}
              data-testid={`slot-telegram-${show}`}
              className="flex items-center gap-1.5 border border-sky-500/50 px-4 py-2 text-xs uppercase tracking-[0.15em] text-sky-400 hover:bg-sky-500/10"
            >
              <Send className="h-3.5 w-3.5" /> Telegram
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  const r = await shareResultImage(show, existing.letter, existing.digits, existing.date, 'facebook');
                  if (r === 'fallback') toast.info('Image downloaded — Facebook-la attach பண்ணுங்க');
                } catch {
                  toast.error('Share failed');
                }
              }}
              data-testid={`slot-facebook-${show}`}
              className="flex items-center gap-1.5 border border-blue-500/50 px-4 py-2 text-xs uppercase tracking-[0.15em] text-blue-400 hover:bg-blue-500/10"
            >
              <Facebook className="h-3.5 w-3.5" /> Facebook
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="mb-2 text-[11px] uppercase tracking-[0.2em] text-[#A3A3A3]">Letter</p>
          <div className="mb-4 flex flex-wrap gap-1.5">
            {LETTERS.map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLetter(l)}
                data-testid={`letter-${show}-${l}`}
                className={`h-9 w-9 border text-sm font-semibold transition-colors ${
                  letter === l
                    ? 'border-[#F5C542] bg-[#F5C542] text-black'
                    : 'border-white/15 bg-black/40 text-white hover:border-[#F5C542]/60'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <p className="mb-2 text-[11px] uppercase tracking-[0.2em] text-[#A3A3A3]">5 Numbers (0-9)</p>
          <div className="mb-5 flex gap-2">
            {digits.map((d, i) => (
              <input
                key={i}
                id={`digit-${show}-${i}`}
                value={d}
                onChange={(e) => setDigit(i, e.target.value.slice(-1))}
                inputMode="numeric"
                maxLength={2}
                data-testid={`digit-${show}-${i}`}
                className="h-12 w-12 border border-white/15 bg-black/60 text-center font-display text-2xl text-white outline-none focus:border-[#F5C542]"
              />
            ))}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              data-testid={`slot-publish-${show}`}
              className="bg-[#F5C542] px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-black transition-colors hover:bg-[#FFDD75] disabled:opacity-50"
            >
              {busy ? 'Saving…' : editing ? 'Update (OK)' : 'Publish (OK)'}
            </button>
            {editing && (
              <button type="button" onClick={() => setEditing(false)} data-testid={`slot-cancel-${show}`} className="border border-white/20 px-4 py-2.5 text-xs uppercase tracking-[0.15em] text-white">
                Cancel
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
